/**
 * FlareHair Theme - Cart Drawer
 * AJAX-powered slide-out cart drawer for Shopify.
 * Works with the markup rendered by snippets/cart-drawer.liquid.
 */

class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    this.overlay = document.getElementById('overlay');
    this.itemsContainer = document.querySelector('.cart-drawer-items');
    this.footerContainer = document.querySelector('.cart-drawer-footer');
    this.cartCountElements = document.querySelectorAll('.cart-count');

    this.isUpdating = false;

    this.bindEvents();
    this.refreshCart();
  }

  /* --------------------------------------------------------------------------
   * open / close
   * ----------------------------------------------------------------------- */

  open() {
    if (!this.drawer || !this.overlay) return;

    this.drawer.classList.add('active');
    this.overlay.classList.add('active');
    document.body.style.overflow = 'hidden';

    document.dispatchEvent(new CustomEvent('cart-drawer:open'));
  }

  close() {
    if (!this.drawer || !this.overlay) return;

    this.drawer.classList.remove('active');
    this.overlay.classList.remove('active');
    document.body.style.overflow = '';

    document.dispatchEvent(new CustomEvent('cart-drawer:close'));
  }

  /* --------------------------------------------------------------------------
   * bindEvents
   * ----------------------------------------------------------------------- */

  bindEvents() {
    // Toggle drawer via data attribute
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-toggle]')) {
        e.preventDefault();
        if (this.drawer && this.drawer.classList.contains('active')) {
          this.close();
        } else {
          this.open();
        }
      }
    });

    // Close button inside drawer
    document.addEventListener('click', (e) => {
      if (e.target.closest('.cart-drawer-close')) {
        this.close();
      }
    });

    // Overlay click closes drawer
    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.close();
      });
    }

    // Intercept add-to-cart form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;

      // Only intercept if cart drawer exists (i.e. cart_type is drawer)
      if (!this.drawer) return;

      e.preventDefault();
      const formData = new FormData(form);
      this.addToCart(formData);
    });

    // Remove item from cart
    document.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('.cart-drawer-item-remove');
      if (!removeBtn) return;

      e.preventDefault();
      const key = removeBtn.getAttribute('data-key');
      if (key) {
        this.removeItem(key);
      }
    });

    // Quantity changes within the drawer (via input change event)
    document.addEventListener('change', (e) => {
      const input = e.target.closest('.cart-drawer .quantity-input');
      if (!input) return;

      const key = input.getAttribute('data-key');
      const quantity = parseInt(input.value, 10);
      if (key && !isNaN(quantity)) {
        this.updateItem(key, Math.max(0, quantity));
      }
    });
  }

  /* --------------------------------------------------------------------------
   * addToCart
   * ----------------------------------------------------------------------- */

  async addToCart(formData) {
    if (this.isUpdating) return;
    this.isUpdating = true;

    try {
      const url = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const message =
          (errorData && errorData.description) ||
          (errorData && errorData.message) ||
          'Could not add item to cart. Please try again.';
        this.showNotification(message, 'error');
        return;
      }

      const addedItem = await response.json().catch(() => ({}));

      this.open();
      await this.refreshCart();

      document.dispatchEvent(
        new CustomEvent('cart:item-added', {
          detail: addedItem
        })
      );
    } catch (error) {
      console.error('[CartDrawer] addToCart error:', error);
      this.showNotification('Something went wrong. Please try again.', 'error');
    } finally {
      this.isUpdating = false;
    }
  }

  /* --------------------------------------------------------------------------
   * updateItem
   * ----------------------------------------------------------------------- */

  async updateItem(key, quantity) {
    if (this.isUpdating) return;
    this.isUpdating = true;

    // Show a loading state
    if (this.itemsContainer) {
      this.itemsContainer.classList.add('cart-drawer-loading');
    }

    try {
      const url = (window.routes && window.routes.cart_change_url) || '/cart/change.js';

      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity }),
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const message =
          (errorData && errorData.description) ||
          (errorData && errorData.message) ||
          'Could not update cart. Please try again.';
        this.showNotification(message, 'error');
        return;
      }

      await this.refreshCart();
    } catch (error) {
      console.error('[CartDrawer] updateItem error:', error);
      this.showNotification('Something went wrong. Please try again.', 'error');
    } finally {
      this.isUpdating = false;
      if (this.itemsContainer) {
        this.itemsContainer.classList.remove('cart-drawer-loading');
      }
    }
  }

  /* --------------------------------------------------------------------------
   * removeItem
   * ----------------------------------------------------------------------- */

  async removeItem(key) {
    await this.updateItem(key, 0);
  }

  /* --------------------------------------------------------------------------
   * refreshCart
   * ----------------------------------------------------------------------- */

  async refreshCart() {
    try {
      const response = await fetch('/cart.js', {
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('[CartDrawer] Failed to fetch cart');
        return;
      }

      const cart = await response.json();
      this.renderCart(cart);
      this.updateCartCount(cart.item_count);
    } catch (error) {
      console.error('[CartDrawer] refreshCart error:', error);
    }
  }

  /* --------------------------------------------------------------------------
   * renderCart
   * ----------------------------------------------------------------------- */

  renderCart(cart) {
    if (!this.itemsContainer || !this.footerContainer) return;

    // Empty cart state
    if (!cart.items || cart.items.length === 0) {
      this.itemsContainer.innerHTML = `
        <div class="cart-drawer-empty">
          <p>Your cart is currently empty.</p>
          <a href="/collections/all" class="btn btn-primary">Continue Shopping</a>
        </div>
      `;
      this.footerContainer.innerHTML = '';
      return;
    }

    // Render items
    const itemsHTML = cart.items
      .map((item, index) => {
        const variantMarkup = item.variant_title
          ? `<p class="cart-drawer-item-variant">${this.escapeHTML(item.variant_title)}</p>`
          : '';

        const imageURL = item.image
          ? item.image.replace(/(\.[^.]+)$/, '_80x80$1')
          : '';

        const imageSrc = item.featured_image && item.featured_image.url
          ? item.featured_image.url
          : imageURL;

        return `
          <div class="cart-drawer-item" data-key="${item.key}">
            <div class="cart-drawer-item-image">
              ${imageSrc
                ? `<img
                    src="${imageSrc}"
                    alt="${this.escapeHTML(item.product_title)}"
                    width="80"
                    height="80"
                    loading="lazy"
                  />`
                : '<div class="cart-drawer-item-placeholder"></div>'
              }
            </div>
            <div class="cart-drawer-item-details">
              <h4 class="cart-drawer-item-title">
                <a href="${this.sanitizeURL(item.url)}">${this.escapeHTML(item.product_title)}</a>
              </h4>
              ${variantMarkup}
              <p class="cart-drawer-item-price">${this.formatMoney(item.final_line_price)}</p>
              <div class="cart-drawer-item-quantity">
                <div class="quantity-selector">
                  <button
                    type="button"
                    class="quantity-btn"
                    aria-label="Decrease quantity for ${this.escapeHTML(item.product_title)}"
                  >
                    <span aria-hidden="true">&minus;</span>
                  </button>
                  <input
                    type="number"
                    class="quantity-input"
                    value="${item.quantity}"
                    min="1"
                    data-key="${item.key}"
                    data-line="${index + 1}"
                    aria-label="Quantity for ${this.escapeHTML(item.product_title)}"
                  />
                  <button
                    type="button"
                    class="quantity-btn"
                    aria-label="Increase quantity for ${this.escapeHTML(item.product_title)}"
                  >
                    <span aria-hidden="true">&plus;</span>
                  </button>
                </div>
              </div>
            </div>
            <button
              type="button"
              class="cart-drawer-item-remove"
              data-key="${item.key}"
              aria-label="Remove ${this.escapeHTML(item.product_title)} from cart"
            >
              &times;
            </button>
          </div>
        `;
      })
      .join('');

    this.itemsContainer.innerHTML = itemsHTML;

    // Render footer
    this.footerContainer.innerHTML = `
      <div class="cart-drawer-subtotal">
        <span>Subtotal</span>
        <span class="cart-drawer-subtotal-price">${this.formatMoney(cart.total_price)}</span>
      </div>
      <p class="cart-drawer-notice">Shipping &amp; taxes calculated at checkout.</p>
      <a href="/checkout" class="btn btn-primary cart-drawer-checkout">Checkout</a>
      <a href="/cart" class="btn btn-secondary cart-drawer-view-cart">View Cart</a>
    `;
  }

  /* --------------------------------------------------------------------------
   * updateCartCount
   * ----------------------------------------------------------------------- */

  updateCartCount(count) {
    // Re-query in case elements were dynamically added
    this.cartCountElements = document.querySelectorAll('.cart-count');

    this.cartCountElements.forEach((el) => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  /* --------------------------------------------------------------------------
   * formatMoney
   * ----------------------------------------------------------------------- */

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const format = window.moneyFormat || '${{amount}}';
    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  }

  /* --------------------------------------------------------------------------
   * escapeHTML - prevent XSS in rendered content
   * ----------------------------------------------------------------------- */

  escapeHTML(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /* --------------------------------------------------------------------------
   * sanitizeURL - prevent javascript: and data: URLs in href attributes
   * ----------------------------------------------------------------------- */

  sanitizeURL(url) {
    if (!url) return '#';
    try {
      const parsed = new URL(url, window.location.origin);
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
        return parsed.href;
      }
      // Relative paths (same origin)
      if (url.startsWith('/')) {
        return url;
      }
      return '#';
    } catch {
      // If it's a simple relative path, allow it
      if (url.startsWith('/') && !url.startsWith('//')) {
        return url;
      }
      return '#';
    }
  }

  /* --------------------------------------------------------------------------
   * showNotification - simple user feedback
   * ----------------------------------------------------------------------- */

  showNotification(message, type = 'info') {
    // Check if there is already a notification container
    let container = document.querySelector('.cart-drawer-notification');
    if (!container) {
      container = document.createElement('div');
      container.className = 'cart-drawer-notification';
      if (this.drawer) {
        this.drawer.prepend(container);
      } else {
        document.body.appendChild(container);
      }
    }

    container.textContent = message;
    container.className = `cart-drawer-notification cart-drawer-notification--${type} active`;

    // Auto-dismiss after 4 seconds
    clearTimeout(this._notificationTimer);
    this._notificationTimer = setTimeout(() => {
      container.classList.remove('active');
    }, 4000);
  }
}

/* --------------------------------------------------------------------------
 * Initialize
 * ----------------------------------------------------------------------- */

document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawer();
});
